import * as React from 'react'
import { ScrollView, Text, View } from 'react-native'
import styled from '@theme/styled-components'

import Picker from '@components/picker/Picker'
import TextInput from '@components/textInput/TextInput'
import Animators from '@components/root/Animators'

import useReducer from '@store/useReducer'
import * as AdminStore from '@store/admin/admin'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'

import { FormikHelpers, useFormik } from 'formik'

import { TypeUtilisateur } from '@api/enum'

interface Values {
  nom: string
  prenom: string
  email: string
  mobile: string
  fonction: string
  societeId?: string
  typeUtilisateur: UserResponse['typeUtilisateur']
  codeMagic?: string
}

const TYPES = ['VIPARIS', 'PRESTATAIRE', 'ORGANISATEUR'] as const

const Info = ({ form, onForm }: AdminUserFormProps) => {
  const init: Values = React.useMemo(
    () => ({
      nom: form.nom,
      prenom: form.prenom,
      email: form.email,
      mobile: form.mobile,
      fonction: form.fonction,
      societeId: form.societeId || '',
      typeUtilisateur: form.typeUtilisateur,
      codeMagic: form.codeMagic || '',
    }),
    [
      form.nom,
      form.prenom,
      form.email,
      form.mobile,
      form.nom,
      form.fonction,
      form.societeId,
      form.typeUtilisateur,
      form.codeMagic,
    ]
  )

  const i18n = useI18n()
  const [, bp] = useBreakpoint()

  const services = useReducer(AdminStore.store, (s) => s.services)

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setFieldValue, setValues, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change, with debounce effect
      const debounce = setTimeout(handleSubmit, 200)
      return () => clearTimeout(debounce)
    }
  }, [values])

  const onTypeUtilisateur = (type: UserResponse['typeUtilisateur']) => {
    setValues({
      ...values,
      typeUtilisateur: type,
      societeId: type === 'PRESTATAIRE' ? values.societeId : '',
      codeMagic: type === 'VIPARIS' ? values.codeMagic : '',
    })
  }

  const typeOptions = TYPES.map((t) => ({ label: i18n.t(`screens.admin.users.form.info.types.${t}`), value: t }))
  const societeOptions = services.map((s) => ({ label: s.nameSociete, value: s.id }))

  return (
    <MainContainer contentContainerStyle={{ paddingHorizontal: bp === 'phone' ? 16 : 45, paddingVertical: 16 }}>
      <Animators>
        <Dual>
          <Column>
            {/* prenom */}
            <Title>{i18n.t('screens.admin.users.form.info.firstname')}</Title>
            <TextInput
              value={values.prenom}
              onChange={(val) => setFieldValue('prenom', val)}
              placeholder={i18n.t('screens.admin.users.form.info.firstname')}
            />
          </Column>

          <LineSeparator />

          <Column>
            {/* nom */}
            <Title>{i18n.t('screens.admin.users.form.info.name')}</Title>
            <TextInput
              value={values.nom}
              onChange={(val) => setFieldValue('nom', val)}
              placeholder={i18n.t('screens.admin.users.form.info.name')}
            />
          </Column>
        </Dual>

        {/* fonction */}
        <Title>{i18n.t('screens.admin.users.form.info.fonction')}</Title>
        <TextInput
          value={values.fonction}
          onChange={(val) => setFieldValue('fonction', val)}
          placeholder={i18n.t('screens.admin.users.form.info.fonction')}
        />

        {/* typeUtilisateur */}
        <Title>{i18n.t('screens.admin.users.form.info.type')}</Title>
        <ZContainer index={2}>
          <Picker
            options={typeOptions}
            value={typeOptions.find((opt) => opt.value === form.typeUtilisateur)}
            onChange={(next) => onTypeUtilisateur(next ? next.value : undefined)}
            placeholder={i18n.t('screens.admin.users.form.info.type')}
            disabled={form.restrictions.some((restr) => restr === 'societeId' || restr === 'manifestations')}
          />
        </ZContainer>

        {/* code magic */}
        <Title>{i18n.t('screens.admin.users.form.info.code')}</Title>
        <TextInput
          value={values.codeMagic}
          onChange={(val) => setFieldValue('codeMagic', val)}
          placeholder={i18n.t('screens.admin.users.form.info.code')}
          disabled={values.typeUtilisateur !== TypeUtilisateur.VIPARIS}
        />

        {/* societe */}
        <Title>{i18n.t('screens.admin.users.form.info.societe')}</Title>
        <ZContainer index={1}>
          <Picker
            options={societeOptions}
            value={societeOptions.find((opt) => opt.value === form.societeId)}
            onChange={(next) => setFieldValue('societeId', next ? next.value : undefined)}
            onClear={() => setFieldValue('societeId', '')}
            placeholder={i18n.t('screens.admin.users.form.info.societe')}
            isClearable
            disabled={values.typeUtilisateur !== TypeUtilisateur.PRESTATAIRE}
          />
        </ZContainer>

        {/* email */}
        <Title>{i18n.t('screens.admin.users.form.info.email')}</Title>
        <TextInput
          value={values.email}
          onChange={(val) => setFieldValue('email', val)}
          placeholder={i18n.t('screens.admin.users.form.info.email')}
        />

        {/* mobile */}
        <Title>{i18n.t('screens.admin.users.form.info.mobile')}</Title>
        <TextInput
          value={values.mobile}
          onChange={(val) => setFieldValue('mobile', val)}
          placeholder={i18n.t('screens.admin.users.form.info.mobile')}
        />
      </Animators>
    </MainContainer>
  )
}

export default Info

const MainContainer = styled(ScrollView)`
  flex: 1;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 5px;
  margin-top: 15px;
`

const ZContainer = styled(View)<{ index: number }>`
  z-index: ${(props) => props.index};
`

const Dual = styled(View)`
  flex-direction: ${(props) => (props.theme.breakpoint === 'phone' ? 'column' : 'row')};
`

const Column = styled(View)`
  flex-direction: column;
  ${(props) => props.theme.breakpoint !== 'phone' && 'flex: 1;'};
`

const LineSeparator = styled(View)`
  width: 20px;
`
